import { useUserSyncContext } from '@/components/auth/UserSyncProvider/index';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { FieldError, fieldErrorProps } from '@/components/ui/field-error';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  type FieldErrors,
  firstInvalidField,
  getFieldError,
  normalizeValidationDetails,
} from '@/lib/validationErrors';
import { useAuth, useUser } from '@clerk/clerk-react';
import {
  ArrowLeft,
  Building2,
  CheckCircle,
  CircleAlert,
  CreditCard,
  Home,
  Loader2,
  MapPin,
  Users,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  continueToAffiliateCheckout,
  getAffiliateCheckoutStorage,
  pollAffiliateCheckoutStatus,
  readAffiliateCheckoutStorage,
  removeAffiliateCheckoutStorage,
  setAffiliateCheckoutStorage,
} from './affiliate-checkout';

const features = [
  'Full access to ErgShoot programming library',
  'Member management dashboard',
  'Gym-wide leaderboards',
  'TV display mode for your gym',
  'Unlimited member accounts',
  'Priority support',
];

const affiliateApplicationFields = new Set([
  'gym_name',
  'website',
  'member_count',
  'owner_name',
  'email',
  'phone',
  'address',
  'city',
  'state',
  'zip_code',
  'country',
  'description',
]);

interface FormData {
  gymName: string;
  ownerName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  memberCount: string;
  website: string;
  description: string;
}

export default function AffiliateApplication() {
  const { isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const { resync } = useUserSyncContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [paymentFeedback, setPaymentFeedback] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>();
  const [formData, setFormData] = useState<FormData>({
    gymName: '',
    ownerName: user?.fullName || '',
    email: user?.primaryEmailAddress?.emailAddress || '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'USA',
    memberCount: '',
    website: '',
    description: '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => {
      if (!prev) return prev;
      const {
        [name]: _camel,
        [name.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)]: _snake,
        ...rest
      } = prev;
      return rest;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setFieldErrors(undefined);

    if (!isSignedIn) {
      // Store form data and redirect to register
      setAffiliateCheckoutStorage(
        getAffiliateCheckoutStorage(),
        'affiliateApplication',
        JSON.stringify(formData)
      );
      navigate('/register?redirect=/apply/affiliate');
      return;
    }

    // Validate required fields
    if (
      !formData.gymName ||
      !formData.ownerName ||
      !formData.email ||
      !formData.city ||
      !formData.state
    ) {
      toast.error('Please fill in all required fields');
      setIsLoading(false);
      return;
    }

    try {
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

      const token = await getToken();
      const response = await fetch(`${API_URL}/public/affiliate-application/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          gym_name: formData.gymName,
          website: formData.website || undefined,
          member_count: formData.memberCount || undefined,
          description: formData.description || undefined,
          owner_name: formData.ownerName,
          email: formData.email,
          phone: formData.phone || undefined,
          address: formData.address || undefined,
          city: formData.city,
          state: formData.state,
          zip_code: formData.zipCode || undefined,
          country: formData.country || 'USA',
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.data?.checkout_url || !data.data?.session_id) {
        const details = normalizeValidationDetails(data.details);
        const field = firstInvalidField(details);
        const fieldId = field?.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
        if (
          details &&
          field &&
          affiliateApplicationFields.has(field) &&
          fieldId &&
          getFieldError(details, fieldId)
        ) {
          setFieldErrors(details);
          requestAnimationFrame(() => document.getElementById(fieldId)?.focus());
          setIsLoading(false);
          return;
        }
        throw new Error(data.error || 'Failed to submit application');
      }

      toast.success('Redirecting to secure payment...');
      continueToAffiliateCheckout({
        storage: getAffiliateCheckoutStorage(),
        application: formData,
        sessionId: data.data.session_id,
        checkoutUrl: data.data.checkout_url,
        navigate: (checkoutUrl) => {
          window.location.href = checkoutUrl;
        },
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to start payment');
      setIsLoading(false);
    }
  };

  // Restore form data if returning from register
  useEffect(() => {
    const savedData = readAffiliateCheckoutStorage(
      getAffiliateCheckoutStorage(),
      'affiliateApplication'
    );
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        setFormData((prev) => ({ ...prev, ...parsed }));
      } catch {
        // Ignore parse errors
      }
    }
  }, []);

  useEffect(() => {
    const checkoutState = searchParams.get('checkout');
    if (checkoutState === 'cancelled') {
      setPaymentFeedback(
        'Payment was cancelled. Your application has not been sent to HQ, and your details are still here when you are ready to try again.'
      );
      setIsLoading(false);
      return;
    }

    if (checkoutState !== 'success' || !isSignedIn) return;

    const sessionId =
      searchParams.get('session_id') ||
      readAffiliateCheckoutStorage(getAffiliateCheckoutStorage(), 'affiliateCheckoutSessionId');
    if (!sessionId) {
      setPaymentFeedback(
        'We could not verify this checkout session. No application has been sent to HQ. Please try again.'
      );
      return;
    }

    let cancelled = false;
    const confirmApplication = async () => {
      setIsLoading(true);
      setPaymentFeedback('Payment received. Confirming your application with Stripe...');
      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

      const status = await pollAffiliateCheckoutStatus({
        apiUrl: API_URL,
        sessionId,
        getToken,
      });

      if (cancelled) return;
      if (status === 'completed') {
        resync();
        setIsSubmitted(true);
        setPaymentFeedback(null);
        setIsLoading(false);
        const storage = getAffiliateCheckoutStorage();
        removeAffiliateCheckoutStorage(storage, 'affiliateApplication');
        removeAffiliateCheckoutStorage(storage, 'affiliateCheckoutSessionId');
        return;
      }
      if (status === 'payment_failed' || status === 'expired') {
        setPaymentFeedback(
          'Payment was not completed. Your application has not been sent to HQ; review your details and try payment again.'
        );
        setIsLoading(false);
        return;
      }

      setPaymentFeedback(
        'Payment is still being confirmed. Your application will only reach HQ after Stripe confirms it; refresh this page in a moment.'
      );
      setIsLoading(false);
    };

    void confirmApplication();
    return () => {
      cancelled = true;
    };
  }, [getToken, isSignedIn, resync, searchParams]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <button
            type="button"
            onClick={() => {
              // BUG-APR08-001: page is often opened in a new tab (target="_blank"
              // from RoleToggleBar), so there's no history to go back to.
              if (window.history.length > 1) navigate(-1);
              else navigate('/');
            }}
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <ArrowLeft className="mr-2 h-5 w-5" />
            Back
          </button>
          <Link
            to="/"
            className="inline-flex items-center font-semibold text-primary hover:text-primary/80 transition-colors"
          >
            <Home className="mr-2 h-5 w-5" />
            Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-primary/10 p-4">
                <Building2 className="h-10 w-10 text-primary" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gradient-gold mb-2">Start a Gym</h1>
            <p className="text-muted-foreground text-lg">
              Apply to become an ErgShoot affiliate and bring elite programming to your members
            </p>
          </div>

          {isSubmitted ? (
            <Card className="max-w-lg mx-auto animate-fade-in">
              <CardContent className="pt-8 pb-8 text-center space-y-4">
                <div className="flex justify-center">
                  <div className="rounded-full bg-green-500/10 p-4">
                    <CheckCircle className="h-12 w-12 text-green-500" />
                  </div>
                </div>
                <h2 className="text-2xl font-bold text-foreground">Application Received</h2>
                <p className="text-muted-foreground text-lg">
                  Payment is confirmed and your application is now with our HQ team. We&apos;ll get
                  back to you within 48 hours.
                </p>
                <p className="text-sm text-muted-foreground">
                  We&apos;ll send updates to <strong>{formData.email}</strong>
                </p>
                <div className="pt-4">
                  <Link to="/">
                    <Button variant="outline">
                      <Home className="h-4 w-4 mr-2" />
                      Back to Home
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ) : (
            <>
              {paymentFeedback && (
                <output className="mb-6 flex items-start gap-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4 text-sm text-yellow-200">
                  {isLoading ? (
                    <Loader2 className="mt-0.5 h-5 w-5 shrink-0 animate-spin" />
                  ) : (
                    <CircleAlert className="mt-0.5 h-5 w-5 shrink-0" />
                  )}
                  <p>{paymentFeedback}</p>
                </output>
              )}
              <div className="grid gap-8 lg:grid-cols-5">
                {/* Application Form */}
                <Card className="lg:col-span-3 animate-fade-in">
                  <CardHeader>
                    <CardTitle>Affiliate Application</CardTitle>
                    <CardDescription>Tell us about your gym</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {/* Gym Information */}
                      <div className="space-y-4">
                        <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                          Gym Information
                        </h3>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="gymName">Gym Name *</Label>
                            <Input
                              id="gymName"
                              name="gymName"
                              value={formData.gymName}
                              onChange={handleChange}
                              placeholder="CrossFit Example"
                              required
                              {...fieldErrorProps(fieldErrors, 'gymName')}
                            />
                            <FieldError errors={fieldErrors} field="gymName" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="website">Website</Label>
                            <Input
                              id="website"
                              name="website"
                              type="url"
                              value={formData.website}
                              onChange={handleChange}
                              placeholder="https://yourgym.com"
                              {...fieldErrorProps(fieldErrors, 'website')}
                            />
                            <FieldError errors={fieldErrors} field="website" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="memberCount">Estimated Members</Label>
                            <Input
                              id="memberCount"
                              name="memberCount"
                              type="number"
                              min="0"
                              step="1"
                              value={formData.memberCount}
                              onChange={handleChange}
                              placeholder="75"
                              {...fieldErrorProps(fieldErrors, 'memberCount')}
                            />
                            <FieldError errors={fieldErrors} field="memberCount" />
                          </div>
                        </div>
                      </div>

                      {/* Owner Information */}
                      <div className="space-y-4">
                        <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                          Owner Information
                        </h3>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2">
                            <Label htmlFor="ownerName">Owner Name *</Label>
                            <Input
                              id="ownerName"
                              name="ownerName"
                              value={formData.ownerName}
                              onChange={handleChange}
                              placeholder="John Smith"
                              required
                              {...fieldErrorProps(fieldErrors, 'ownerName')}
                            />
                            <FieldError errors={fieldErrors} field="ownerName" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email">Email *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="john@yourgym.com"
                              required
                              {...fieldErrorProps(fieldErrors, 'email')}
                            />
                            <FieldError errors={fieldErrors} field="email" />
                          </div>
                          <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="phone">Phone</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="(555) 123-4567"
                              {...fieldErrorProps(fieldErrors, 'phone')}
                            />
                            <FieldError errors={fieldErrors} field="phone" />
                          </div>
                        </div>
                      </div>

                      {/* Location */}
                      <div className="space-y-4">
                        <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                          <MapPin className="h-4 w-4" />
                          Location
                        </h3>
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div className="space-y-2 sm:col-span-2">
                            <Label htmlFor="address">Street Address</Label>
                            <Input
                              id="address"
                              name="address"
                              value={formData.address}
                              onChange={handleChange}
                              placeholder="123 Main St"
                              {...fieldErrorProps(fieldErrors, 'address')}
                            />
                            <FieldError errors={fieldErrors} field="address" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="city">City *</Label>
                            <Input
                              id="city"
                              name="city"
                              value={formData.city}
                              onChange={handleChange}
                              placeholder="Austin"
                              required
                              {...fieldErrorProps(fieldErrors, 'city')}
                            />
                            <FieldError errors={fieldErrors} field="city" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="state">State *</Label>
                            <Input
                              id="state"
                              name="state"
                              value={formData.state}
                              onChange={handleChange}
                              placeholder="TX"
                              required
                              {...fieldErrorProps(fieldErrors, 'state')}
                            />
                            <FieldError errors={fieldErrors} field="state" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="zipCode">ZIP Code</Label>
                            <Input
                              id="zipCode"
                              name="zipCode"
                              value={formData.zipCode}
                              onChange={handleChange}
                              placeholder="78701"
                              {...fieldErrorProps(fieldErrors, 'zipCode')}
                            />
                            <FieldError errors={fieldErrors} field="zipCode" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="country">Country</Label>
                            <Input
                              id="country"
                              name="country"
                              value={formData.country}
                              onChange={handleChange}
                              placeholder="USA"
                              {...fieldErrorProps(fieldErrors, 'country')}
                            />
                            <FieldError errors={fieldErrors} field="country" />
                          </div>
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div className="space-y-4">
                        <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                          Additional Information
                        </h3>
                        <div className="space-y-2">
                          <Label htmlFor="description">Tell us about your gym</Label>
                          <Textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="What makes your gym unique? What are your goals with ErgShoot?"
                            rows={4}
                            {...fieldErrorProps(fieldErrors, 'description')}
                          />
                          <FieldError errors={fieldErrors} field="description" />
                        </div>
                      </div>

                      <Button type="submit" disabled={isLoading} className="w-full" size="lg">
                        {isLoading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <CreditCard className="h-4 w-4 mr-2" />
                        )}
                        {isSignedIn ? 'Continue to Secure Payment' : 'Create Account & Continue'}
                      </Button>

                      <p className="text-xs text-center text-muted-foreground">
                        Your application is sent to HQ only after Stripe confirms payment. By
                        continuing, you agree to our terms of service.
                      </p>
                    </form>
                  </CardContent>
                </Card>

                {/* Pricing & Features */}
                <div className="lg:col-span-2 space-y-6">
                  <Card
                    className="border-primary/50 animate-fade-in"
                    style={{ animationDelay: '100ms' }}
                  >
                    <CardHeader className="text-center pb-4">
                      <CardTitle className="text-xl">Affiliate Plan</CardTitle>
                      <CardDescription>Everything your gym needs</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                      <div className="text-center">
                        <div className="text-5xl font-bold text-primary">£67.15</div>
                        <p className="text-muted-foreground">/month</p>
                      </div>

                      <ul className="space-y-3">
                        {features.map((feature, index) => (
                          <li
                            key={feature}
                            className="flex items-center gap-2 text-sm animate-fade-in"
                            style={{ animationDelay: `${(index + 2) * 50}ms` }}
                          >
                            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>

                  <Card className="animate-fade-in" style={{ animationDelay: '200ms' }}>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Users className="h-5 w-5 text-primary" />
                        Member Pricing
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground">
                        Your members get free access when they join through your gym. No additional
                        per-member fees.
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </>
          )}

          {/* FAQ */}
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground">
              Questions about becoming an affiliate?{' '}
              <a href="mailto:affiliates@ergshoot.com" className="text-primary hover:underline">
                affiliates@ergshoot.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
